import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import {
	ChartTypes,
	Subcharts,
	Timeframes,
} from "@/components/widgets/Chart/types";
import { CheckboxPopover } from "@/components/widgets/Chart/ui/ChartDropdown/CheckboxPopover";
import {
	categoryDropdownConfig,
	datasetDropdownConfig,
	sectorDropdownConfig,
} from "@/components/widgets/News/dropdownsConfig";
import type {
	NEWS_CATEGORY,
	NEWS_DATASET,
	NEWS_SECTOR,
} from "@/components/widgets/News/types";
import { CheckboxPopover as NewsCheckboxPopover } from "@/components/widgets/News/ui/components/CheckboxPopover";
import { scorerTotal } from "@/components/widgets/Screener/ScreenerScorer/helpers/addScoringColumns/addScoringColumns";
import { useAuth } from "@/hooks/useAuth";
import { useAuthService } from "@/hooks/useAuthService";
import { isEnabledAdvancedChart, isEnabledScorer } from "@/lib/utils/features";
import AppLogo from "@/shared/assets/icons/ui/logo.svg?react";
import { HoverCardWrapper } from "@/shared/components/HoverCardWrapper";
import { OfferToUpgrade } from "@/shared/components/OfferToUpgrade";
import { StateManagement } from "@/shared/components/StateManagement/ui/StateManagement";
import { DEFAULT_TICKER } from "@/shared/constants/common";
import { KEY } from "@/shared/constants/keyboard";
import { EVENTS } from "@/shared/eventEmiters/consts";
import type { EventEmitter } from "@/shared/eventEmiters/EventEmitter";
import type { ViewId } from "@/shared/types/common";
import sprites from "/src/shared/assets/sprite.svg";
import { ExpandButton } from "../../../ui/expand-button";
import { ChartDropdown } from "../../../widgets/Chart/ui/ChartDropdown/ChartDropdown";
import { FinancialsDropdown } from "../../../widgets/Financials/ui/components/FinancialsDropdown";
import { NewsDropdown } from "../../../widgets/News/ui/components/NewsDropdown";
import { NewsTicker } from "../../../widgets/News/ui/components/NewsTicker";
import { PeadHeader } from "../../../widgets/Pead/ui/components/PeadHeader/ui/PeadHeader";
import { Ticker } from "../../../widgets/Ticker/Ticker";
import { useExpandedWindow } from "../../hooks/useExpandedWindow";

interface ITileTitle {
	baseId: ViewId;
	id: string;
	ticker: string;
	eventEmitter: EventEmitter;
	activeTabId?: string;
	handleTickerChange: (id: string, value: string) => void;
	handleOpenNavigation: (id: string) => void;
}

export type DropdownProps = {
	state?: Record<string, unknown>;
	onStateChange: OnStateChange;
};

export type OnStateChange = (
	state: Record<string, unknown>,
	shouldRerender?: boolean,
) => void;

export const TileTitle = ({
	baseId,
	id,
	ticker,
	eventEmitter,
	handleTickerChange,
	handleOpenNavigation,
	onStateChange,
	state,
	activeTabId,
}: ITileTitle & DropdownProps) => {
	const { isExpanded, toggleExpanded } = useExpandedWindow();
	const { isAuthenticated } = useAuth();
	const isNavigationAllowed = useAuthService().isNavigationButtonAllowed;

	const isAdvancedChart =
		isEnabledAdvancedChart() &&
		id.includes("Chart") &&
		(state?.type ?? ChartTypes.ADVANCED) === ChartTypes.ADVANCED;

	const isScorerEnabled = isEnabledScorer();

	const viewId: ViewId = baseId === "Advanced Quote" ? "Quote" : baseId;

	return (
		<div
			className="text-xs h-full w-full flex items-center bg-(--dark-background)"
			title=""
		>
			<HoverCardWrapper
				trigger={
					<div
						data-testid="navigation-icon"
						className="flex items-center cursor-pointer"
						onClick={() => isNavigationAllowed && handleOpenNavigation(id)}
						onKeyDown={(e) => {
							if (e.key === KEY.ENTER) {
								isNavigationAllowed && handleOpenNavigation(id);
							}
						}}
					>
						<Tooltip>
							<TooltipTrigger asChild>
								<AppLogo />
							</TooltipTrigger>
							<TooltipContent>Open navigation</TooltipContent>
						</Tooltip>
					</div>
				}
				content={
					<OfferToUpgrade
						offer={<p>To open the Navigation, please upgrade your plan</p>}
					/>
				}
				disabled={!isNavigationAllowed}
			/>

			<p className="font-bold pr-4 pl-2">{viewId}</p>

			{["Drift Forecast", "Earnings Analysis", "Holders"].includes(baseId) &&
			!isAuthenticated ? null : (
				<>
					{id.includes("News") && (
						<div className="flex gap-2 h-fit">
							<NewsTicker tileId={id} onChange={handleTickerChange} />

							<NewsDropdown
								tileId={id}
								state={state}
								onStateChange={onStateChange}
							/>

							<NewsCheckboxPopover
								options={Object.values(datasetDropdownConfig)}
								checkedOptions={(state?.datasetFilters as NEWS_DATASET[]) ?? []}
								buttonText="Apply"
								displayText="Type"
								stateKey="datasetFilters"
								onStateChange={onStateChange}
							/>

							<NewsCheckboxPopover
								options={Object.values(categoryDropdownConfig)}
								checkedOptions={
									(state?.categoryFilters as NEWS_CATEGORY[]) ?? []
								}
								buttonText="Apply"
								displayText="Category"
								stateKey="categoryFilters"
								onStateChange={onStateChange}
							/>

							<NewsCheckboxPopover
								options={Object.values(sectorDropdownConfig)}
								checkedOptions={(state?.sectorFilters as NEWS_SECTOR[]) ?? []}
								buttonText="Apply"
								displayText="Sectors"
								stateKey="sectorFilters"
								onStateChange={onStateChange}
							/>
						</div>
					)}

					<div className="flex gap-2 items-center">
						{[
							{
								id: "Screener",
								content: (
									<svg
										aria-label="filter"
										role="img"
										stroke={isExpanded ? "var(--gold)" : "#fff"}
										className="h-4 w-4"
									>
										<use xlinkHref={`${sprites}#screener-panel`} />
									</svg>
								),
							},
							{
								id: "Monitor",
								content: (
									<svg
										aria-label="columns"
										role="img"
										stroke={isExpanded ? "var(--gold)" : "#fff"}
										className="h-4 w-4"
									>
										<use xlinkHref={`${sprites}#screener-panel`} />
									</svg>
								),
							},
						].map(
							(btn) =>
								baseId === btn.id && (
									<ExpandButton
										key={btn.id}
										id={btn.id}
										content={btn.content}
										eventEmitter={eventEmitter}
										onClick={toggleExpanded}
									/>
								),
						)}

						{["Monitor", "Screener"].includes(baseId) && (
							<StateManagement
								widgetName={baseId}
								eventEmitter={eventEmitter}
								state={state}
								onStateChange={onStateChange}
								key={activeTabId}
							/>
						)}

						{id.includes("Screener") && (
							<div className="flex gap-2 ml-1">
								<p
									className={`mr-2 ${isScorerEnabled ? "cursor-pointer" : "cursor-not-allowed"} ${isScorerEnabled && state?.isScorerOpened ? "" : "opacity-30"}`}
									onClick={() => {
										if (isScorerEnabled) {
											onStateChange({ isScorerOpened: !state?.isScorerOpened });
											if (state?.isScorerOpened) {
												eventEmitter.emit(EVENTS.SCORER, false);
											} else {
												eventEmitter.emit(EVENTS.SORT, [
													{ name: scorerTotal.name, order: 1 },
												]);
											}
										}
									}}
									onKeyDown={(e) => {
										if (e.key === KEY.ENTER && isScorerEnabled) {
											onStateChange({ isScorerOpened: !state?.isScorerOpened });
											if (state?.isScorerOpened) {
												eventEmitter.emit(EVENTS.SCORER, false);
											} else {
												eventEmitter.emit(EVENTS.SORT, [
													{ name: scorerTotal.name, order: 1 },
												]);
											}
										}
									}}
								>
									Scorer
								</p>
								<p className="cursor-not-allowed opacity-30">Back-test</p>
							</div>
						)}
					</div>

					{![
						"Screener",
						"Monitor",
						"News",
						"Chat",
						"Backtest",
						"Markets",
					].includes(baseId) && (
						<Ticker
							ticker={ticker || DEFAULT_TICKER}
							onChange={(value) => handleTickerChange(id, value)}
						/>
					)}

					{id.includes("Chart") && (
						<>
							<div className="flex gap-2">
								<ChartDropdown
									enumType={Timeframes}
									value={(state?.timeFrame as Timeframes) ?? Timeframes.ONE_DAY}
									stateKey="timeFrame"
									onStateChange={onStateChange}
								/>
							</div>

							{isEnabledAdvancedChart() && (
								<div className="flex gap-2">
									<ChartDropdown
										enumType={ChartTypes}
										value={(state?.type as ChartTypes) ?? ChartTypes.ADVANCED}
										stateKey="type"
										onStateChange={onStateChange}
									/>
								</div>
							)}

							{isAdvancedChart && (
								<div className="flex gap-2 !w-[70px]">
									<CheckboxPopover
										arrayType={Object.values(Subcharts)}
										checkedValues={(state?.panels as Subcharts[]) ?? []}
										buttonText="Apply"
										displayText="Signals"
										stateKey="panels"
										applyStateKey="applyPanels"
										onStateChange={onStateChange}
									/>
								</div>
							)}
						</>
					)}

					{id.includes("Drift Forecast") && (
						<PeadHeader
							widgetId={id}
							state={state}
							onStateChange={onStateChange}
						/>
					)}

					{id.includes("Financials") && (
						<FinancialsDropdown state={state} onStateChange={onStateChange} />
					)}
				</>
			)}
		</div>
	);
};
